<template>
  <section class="">
    <Modal
      v-model:show="isEdit"
      class="modal-alert edit-general"
      @close="isEdit = false"
    >
      <template v-slot:header>
        <h5 class="modal-title">
          {{ $t('profile.generalInfo.title_edit') }}
        </h5>
      </template>

      <div class="tw-grid tw-grid-cols-12 tw-gap-x-23 tw-gap-y-16">
        <div class="md:tw-col-span-6 tw-col-span-6">
          <Tooltip
            :disabled="!isFieldLocked('first_name')"
            :title="$t('profile.generalInfo.verifiedReadonlyTooltip')"
          >
            <UiInput
              v-model="form.first_name"
              v-model:error="errors_.first_name"
              :label="$t('profile.generalInfo.firstName')"
              :placeholder="$t('profile.generalInfo.notSpecified')"
              :disabled="isFieldLocked('first_name')"
              error-clear-on-change
            />
          </Tooltip>
        </div>

        <!--Last name-->
        <div class="md:tw-col-span-6 tw-col-span-6">
          <Tooltip
            :disabled="!isFieldLocked('last_name')"
            :title="$t('profile.generalInfo.verifiedReadonlyTooltip')"
          >
            <UiInput
              v-model="form.last_name"
              v-model:error="errors_.last_name"
              :label="$t('profile.generalInfo.lastName')"
              :placeholder="$t('profile.generalInfo.notSpecified')"
              :disabled="isFieldLocked('last_name')"
              error-clear-on-change
            />
          </Tooltip>
        </div>

        <!--date of birth-->
        <div class="sm:tw-col-span-6 tw-col-span-12">
          <Tooltip
            :disabled="!isFieldLocked('date_of_birth')"
            :title="$t('profile.generalInfo.verifiedReadonlyTooltip')"
          >
            <UiDatePicker
              v-model="birthDate"
              v-model:error="errors_.date_of_birth"
              :default-value="defaultDateView"
              :disabled-date="disabledDate"
              :disabled="isFieldLocked('date_of_birth')"
              :label="$t('profile.generalInfo.birthDate')"
              :placeholder="$t('profile.generalInfo.notSpecified')"
            />
          </Tooltip>
        </div>

        <div class="tw-col-span-12 profile__general-banddown">
          <hr>
        </div>

        <!--contact number-->
        <div class="sm:tw-col-span-6 tw-col-span-12 tw-mt-r5">
          <Tooltip
            :disabled="!isFieldLocked('phone_number')"
            :title="$t('profile.generalInfo.verifiedReadonlyTooltip')"
          >
            <UiInputPhone
              v-if="isEdit"
              v-model="form.phone_number"
              v-model:error="errors_.phone_number"
              :initial-country="form.country_code"
              :label="$t('profile.generalInfo.contactNumber')"
              :placeholder="$t('profile.generalInfo.notSpecified')"
              :disabled="isFieldLocked('phone_number')"
            />
          </Tooltip>
        </div>

        <div class="tw-col-span-6 sm:tw-block tw-hidden" />

        <div class="md:tw-col-span-6 tw-col-span-6 select-wrapper">
          <UiSelect
            v-model="form.messenger"
            :label="$t('profile.generalInfo.messenger')"
            :list="messengers"
            :placeholder="$t('profile.generalInfo.notSpecified')"
            is-search
          />
        </div>

        <!--messenger id-->
        <div class="md:tw-col-span-6 tw-col-span-6">
          <UiInput
            v-model="form.messenger_id"
            v-model:error="errors_.messenger_id"
            :label="$t('profile.generalInfo.messengerId')"
            :placeholder="messengerIdPlaceholder"
            error-clear-on-change
          />
        </div>

        <div class="tw-col-span-12 profile__general-banddown">
          <hr>
        </div>

        <!--country-->
        <div class="md:tw-col-span-6 tw-col-span-6 tw-mt-r5 select-wrapper profile__select-disable">
          <Tooltip
            :disabled="!isFieldLocked('country_code')"
            :title="$t('profile.generalInfo.verifiedReadonlyTooltip')"
          >
            <UiSelect
              v-model="form.country_code"
              :label="$t('profile.generalInfo.country')"
              :list="countries"
              :placeholder="$t('profile.generalInfo.notSpecified')"
              :disabled="isFieldLocked('country_code')"
              is-search
            />
          </Tooltip>
        </div>

        <!--city-->
        <div class="md:tw-col-span-6 tw-col-span-6 tw-mt-r5">
          <UiInput
            v-model="form.city"
            :label="$t('profile.generalInfo.city')"
            :placeholder="$t('profile.generalInfo.notSpecified')"
          />
        </div>

        <!--address-->
        <div class="tw-col-span-12">
          <UiInput
            v-model="form.address"
            v-model:error="errors_.address"
            :label="$t('profile.generalInfo.address')"
            :placeholder="$t('profile.generalInfo.notSpecified')"
            error-clear-on-change
          />
        </div>

        <!--zip code-->
        <div class="tw-col-span-12">
          <div class="sm:tw-w-1/2 tw-w-full">
            <UiInput
              v-model="form.postal_code"
              v-model:error="errors_.postal_code"
              :label="$t('profile.generalInfo.postal_code')"
              :placeholder="$t('profile.generalInfo.notSpecified')"
              error-clear-on-change
            />
          </div>
        </div>

        <div class="tw-col-span-12 profile__general-banddown">
          <hr>
        </div>

        <!--timezone-->
        <div class="md:tw-col-span-6 tw-col-span-6 tw-mt-r5 select-wrapper profile__select-disable">
          <UiSelect
            v-model="form.timezone"
            :label="$t('profile.generalInfo.timezone')"
            :list="timezones"
            :placeholder="$t('profile.generalInfo.notSpecified')"
            is-search
          />
        </div>

        <!--first day of week-->
        <div class="md:tw-col-span-6 tw-col-span-6 tw-mt-r5 select-wrapper profile__select-disable">
          <UiSelect
            v-model="form.first_day_of_week"
            :label="$t('profile.generalInfo.firstDay')"
            :list="days"
            :placeholder="$t('profile.generalInfo.notSpecified')"
            is-search
          />
        </div>
      </div>

      <template v-slot:footer>
        <div class="!tw-flex !tw-justify-end !tw-w-full mt-8 mr-0">
          <button
            class="btn btn-outline-primary btn-sm btn__back"
            @click="close"
          >
            {{ $t('profile.buttons.cancel') }}
          </button>
          <button
            :disabled="isLoading"
            class="btn btn-primary btn-sm ml-16"
            @click="save"
          >
            {{ $t('profile.buttons.save') }}
          </button>
        </div>
      </template>
    </Modal>
  </section>
</template>

<script>
import moment from 'moment/moment';
import { Modal } from '@/components';
import Tooltip from '@/components/Tooltip.vue';

const days = [
  {
    id: 'monday',
    text: 'profile.generalInfo.days.mo',
  },
  {
    id: 'sunday',
    text: 'profile.generalInfo.days.su',
  },
];

export default {
  components: {
    Modal,
    Tooltip,
  },
  data() {
    return {
      isEdit: false,
      locale: '',

      form: {},
      loading: false,
      isLoading: false,
      birthDate: '',

      defaultDateView: moment().subtract(18, 'year').toDate(),
      disabledDate: (time) => {
        return time.setFullYear(time.getFullYear() + 18) > Date.now();
      },
      days: days.map((d) => ({
        ...d,
        text: this.$t(d.text),
      })),
      errors_: {},
    };
  },
  computed: {
    messengers() {
      return this.$store.state.misc.messengers.map((e) => ({
        id: e.id,
        text: e.name,
      }));
    },
    messengerIdPlaceholder() {
      if (!this.form.messenger_id) {
        return (
          this.$store.state.misc.messengers.find((e) => e.id === this.form.messenger)?.example_placeholder ||
          this.$t('profile.generalInfo.notSpecified')
        );
      }
      return '';
    },
    countries() {
      return this.$store.state.misc.countries.map((e) => {
        e.id = e.code;
        e.text = `${e.name} - ${e.name_native}`;
        return e;
      });
    },
    timezones() {
      return this.$store.state.misc.timezones.map((e) => {
        e.id = e.zone;
        e.text = e.name;
        return e;
      });
    },
  },
  watch: {
    locale() {
      this.days.forEach((d, i) => (d.text = this.$t(days[i].text)));
      this.days = [...this.days];
    },
  },
  updated() {
    this.locale = this.$locale.value;
  },
  methods: {
    isFieldLocked(field) {
      return (this.$store.state.settings?.kyc?.locked_fields || []).includes(field);
    },
    open(data) {
      this.errors_ = {};
      this.isEdit = true;
      this.loading = true;

      const { user_date_of_birth, ...restData } = data;
      this.form = restData;
      this.birthDate = user_date_of_birth;
    },
    close() {
      this.isEdit = false;
    },

    save() {
      this.errors_ = {};

      const data = {
        ...this.$_.cloneDeep(this.form),
        phone_number: this.form.phone_number,
        date_of_birth: this.birthDate && moment(this.birthDate).format('YYYY-MM-DD'),
      };

      this.isLoading = true;

      this.$store
        .dispatch('saveProfile', data)
        .then(() => {
          this.$alert.success(this.$t('profile.success.saved'));
          this.isEdit = false;
        })
        .catch((err) => {
          const errors = err.response.data.errors;

          if (Object.keys(errors).length) {
            this.errors_ = errors;
          } else {
            this.$alert.fail(this.$t('profile.errors.incomplete_profile'));
          }
        })
        .finally(() => this.isLoading = false);
    },
  },
};
</script>

<style lang="scss" scoped>
.form-error {
  font-size: 12px;
  top: -5px;
  position: relative;
}

.edit-general :deep(.modal-dialog) {
  flex-grow: 1;
}
</style>
